import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Link } from 'react-router-dom';
import scrollbar from '../../styles/scrollbar';
import RobotIcon from '../icons/RobotIcon';
import { useT } from '../../i18n';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * The desktop panel takes the room the board is not using, and no more.
 *
 * Board.js caps the desktop board at `calc(100dvh - 56px)`, so on any window
 * wider than it is tall the board is limited by height and the width past
 * that cap is slack — the panel can spend it without the board losing a
 * pixel. Below that the board is the thing being squeezed, and the panel
 * holds at the width it has always had.
 *
 * clamp() states exactly that, and does it continuously: no breakpoint to
 * pop across, and the middle of the range settles where the board sits
 * precisely at its cap. BOARD_GUTTER is boardArea's desktop padding in
 * Game.js (24px a side), and BOARD_CAP_TRIM the 56px Board.js already
 * subtracts for it — err high rather than low, since overshooting is what
 * would cost the board room.
 */
export const SIDEBAR_WIDTH = 330;
export const SIDEBAR_WIDTH_WIDE = 380;
const BOARD_GUTTER = 48;
const BOARD_CAP_TRIM = 56;

const useStyles = createUseStyles({
  desktopPanel: {
    width: `clamp(${SIDEBAR_WIDTH}px, calc(100vw - 100dvh + ${BOARD_CAP_TRIM - BOARD_GUTTER}px), ${SIDEBAR_WIDTH_WIDE}px)`,
    flexShrink: 0,
    boxSizing: 'border-box',
    background: ({ theme }) => theme.colorPanel,
    borderLeft: '1px solid #6b4622',
    boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '16px',
    overflowY: 'auto',
    ...scrollbar,
  },
  logo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    paddingBottom: '12px',
    borderBottom: '1px solid #4a3020',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { filter: 'brightness(1.15)' },
    '&:active': { filter: 'none' },
  },
  marks: {
    display: 'flex',
    gap: '7px',
  },
  mark: {
    width: '20px',
    height: '20px',
  },
  title: {
    margin: 0,
    fontSize: '19px',
    fontWeight: '700',
    color: '#f5e6c8',
    letterSpacing: '3.5px',
    textTransform: 'uppercase',
    textShadow: '0 1px 0 rgba(0,0,0,0.5)',
  },
  subtitle: {
    margin: 0,
    fontFamily: MONO,
    fontSize: '9px',
    color: '#c4a070',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  startBtn: {
    padding: '15px 24px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    color: '#fff3e6',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '15px',
    fontFamily: 'inherit',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    alignSelf: 'stretch',
    marginTop: '0',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -3px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    transition: 'background .12s ease, transform .06s ease, box-shadow .06s ease',
    '&:hover': {
      background: '#e35c36',
    },
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 2px 5px rgba(0,0,0,0.4)',
    },
  },
  // A host alone in the lobby has no way to tell whether the button will do
  // anything. It will: the round starts, and late arrivals are seated as they
  // come. 5.4:1 on the panel — do not darken it.
  startHint: {
    margin: '6px 0 0',
    textAlign: 'center',
    fontSize: '10.5px',
    lineHeight: 1.35,
    color: '#a8875d',
  },
  shuffleBtn: {
    padding: '11px 24px',
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '9px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '12.5px',
    fontFamily: 'inherit',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    alignSelf: 'stretch',
    marginTop: 'auto',
    marginBottom: '8px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 0 rgba(0,0,0,0.4)',
    '&:hover': { background: '#4a3220' },
    '&:active': { boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)' },
  },
});

function Sidebar({ children, phase, selfId, hostId, onStartGame, onReshuffleBoard, onLeave }) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const t = useT();
  const isHost = selfId === hostId;

  return (
    <div className={classes.desktopPanel}>
      <Link className={classes.logo} to="/" title={t('a11y.backToMenu')} onClick={onLeave}>
        <div className={classes.marks}>
          {ROBOT_COLORS.map(c => (
            <div key={c} className={classes.mark}>
              <RobotIcon color={c} />
            </div>
          ))}
        </div>
        <h1 className={classes.title}>Robotrons</h1>
        <p className={classes.subtitle}>{t('menu.subtitle')}</p>
      </Link>
      {children}
      {phase === 'LOBBY' && isHost && (
        <>
          <button className={classes.shuffleBtn} onClick={onReshuffleBoard}>
            {t('game.shuffleBoard')}
          </button>
          <button className={classes.startBtn} onClick={onStartGame}>
            {t('game.startGame')}
          </button>
          <p className={classes.startHint}>{t('game.startHint')}</p>
        </>
      )}
    </div>
  );
}

export default Sidebar;
